import type { ProductId } from "../products/registry";
import { AUTHORITIES, GROUP_AUTHORITIES, type Authority } from "./authorities";
import { GROUP_LEVEL, LEVELS, type Level } from "./levels";

/**
 * Every action this console is designed to offer, and what it actually takes.
 *
 * The design's permission map is a list of ACTIONS with a minimum level —
 * "Set tier for a tenant · L2" — which is how an operator thinks. This console
 * gates on AUTHORITIES from the `cognito:groups` claim, which is what the API
 * gateway is to check. Both are true, and they are not the same table.
 *
 * So each row names the authority, and the level is DERIVED from it — the
 * lowest group holding that authority, via the ladder in levels.ts. That means
 * the level shown can never drift from what a write will actually be allowed
 * to do, which a second hand-maintained column certainly would.
 *
 * WHERE THE TWO DISAGREE, THE ROW SAYS SO. The design puts "Set tier for a
 * tenant" at L2; the deployed groups give `tenant:write` to admin-operator,
 * which is L3. That is a real gap between the design's intent and the pool
 * that exists, and an operator who is refused a write the map promised them
 * needs to see it rather than conclude the console is broken. `designMin`
 * exists only to be compared.
 *
 * `needs: null` means nothing here enforces it, for one of three reasons the
 * row distinguishes: the product is unwired (Labeeb), the route does not exist
 * (MCP has no per-tenant quota override), or the capability is absent by
 * construction (the ("*","*") blocklist row).
 */
export type Scope = "tenant" | "fleet" | "fleet-draft" | "any";

export interface Action {
  area: string;
  action: string;
  /** The design's story id, so a row can be traced back to the spec. */
  story: string;
  scope: Scope;
  /** What the server checks. Null when nothing here enforces it — see `why`. */
  needs: Authority | null;
  /** Who would enforce it. "platform" is Cognito itself. */
  product: ProductId | "platform";
  /** The minimum level the DESIGN specifies. Compared, never enforced. */
  designMin: Level | "—";
  /**
   * The design requires a second person. This console CANNOT enforce that —
   * it is a static page holding your token, and anyone who could complete a
   * ceremony here can call the same endpoint with curl. Rendered as what the
   * design asks for, never as a control that exists.
   */
  dual: boolean;
  /** Why nothing enforces it, when `needs` is null. */
  why?: string;
  /** No level reaches it, and none ever will — absent by construction. */
  blocked?: boolean;
}

export const ACTIONS: Action[] = [
  // ------------------------------------------------------------ Tenant 360 --
  { area: "Tenant 360", action: "View tenant, evidence, effective values", story: "S1.1", scope: "tenant", needs: "tenant:read", product: "mcp", designMin: "L1", dual: false },
  { area: "Tenant 360", action: "See fleet-wide totals", story: "S1.2", scope: "fleet", needs: "fleet:read", product: "mcp", designMin: "L1", dual: false },

  // --------------------------------------------------------- Entitlements --
  { area: "Entitlements", action: "Set tier for a tenant", story: "S2.1–2.2", scope: "tenant", needs: "tenant:write", product: "mcp", designMin: "L2", dual: false },
  { area: "Entitlements", action: "Edit what a tier means", story: "S2.4", scope: "fleet", needs: null, product: "labeeb", designMin: "L3", dual: true, why: "the tier envelope is Labeeb's and no /labeeb route is mounted" },

  // --------------------------------------------------------------- Quotas --
  { area: "Quotas", action: "See tool caps and the catalog", story: "S3.1", scope: "fleet", needs: "quota:read", product: "mcp", designMin: "L1", dual: false },
  { area: "Quotas", action: "Per-tool cap for a tier", story: "S3.2", scope: "fleet", needs: "quota:write", product: "mcp", designMin: "L3", dual: false },
  { area: "Quotas", action: 'tool = "*" default cap', story: "S3.4", scope: "fleet", needs: "quota:write", product: "mcp", designMin: "L3", dual: true },
  { area: "Quotas", action: "Cap a search_* tool", story: "S3.3", scope: "fleet", needs: "quota:write", product: "mcp", designMin: "L3", dual: true },
  { area: "Quotas", action: "Override the search_* guard", story: "S3.3", scope: "fleet", needs: "quota:force", product: "mcp", designMin: "L3", dual: true },
  { area: "Quotas", action: "Remove a cap override", story: "S3.7", scope: "fleet", needs: "quota:write", product: "mcp", designMin: "L3", dual: false },
  { area: "Quotas", action: "Per-tenant override, ≤30d expiry", story: "S3.5", scope: "tenant", needs: null, product: "mcp", designMin: "L2", dual: false, why: "caps are per TIER on this API — there is no per-tenant quota route, and no expiry concept" },
  { area: "Quotas", action: "Promote alert-only → enforcing", story: "S3.6", scope: "fleet", needs: null, product: "mcp", designMin: "L3", dual: true, why: "an MCP cap enforces the moment it is written; there is no alert-only mode to promote from" },

  // --------------------------------------------------------------- Access --
  { area: "Access", action: "Block / unblock a merchant", story: "S4.1", scope: "tenant", needs: "block:write", product: "mcp", designMin: "L2", dual: false },
  { area: "Access", action: "Block one email in one merchant", story: "S4.2", scope: "tenant", needs: "block:write", product: "mcp", designMin: "L2", dual: false },
  { area: "Access", action: "Block an email across all merchants", story: "S4.2", scope: "fleet", needs: "block:write", product: "mcp", designMin: "L3", dual: true },
  { area: "Access", action: '("*","*") on the blocklist', story: "S4.3", scope: "fleet", needs: null, product: "mcp", designMin: "—", dual: false, blocked: true, why: "the schema accepts the row; no authority reaches it and no form here composes one" },
  { area: "Access", action: "Suspend a Labeeb tenant", story: "S4.4", scope: "tenant", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "suspension is Labeeb's; blocking here stops MCP tool calls only" },

  // --------------------------------------------------------------- Agents --
  { area: "Agents", action: "AllowedAgents for one tenant", story: "S5.1", scope: "tenant", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "no /labeeb route is mounted" },
  { area: "Agents", action: "BetaAgents for one tenant", story: "S5.2", scope: "tenant", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "no /labeeb route is mounted" },
  { area: "Agents", action: "Beta cohort apply", story: "S5.3", scope: "fleet", needs: null, product: "labeeb", designMin: "L3", dual: false, why: "no /labeeb route is mounted" },

  // -------------------------------------------------------------- AI apps --
  // L3, not the L2 of a runtime setting: allowing an app hands merchants'
  // tokens to whoever controls its address.
  { area: "AI apps", action: "See which AI apps may connect", story: "S6.3", scope: "fleet", needs: "settings:read", product: "mcp", designMin: "L1", dual: false },
  { area: "AI apps", action: "Allow an AI app", story: "S6.3", scope: "fleet", needs: "settings:write", product: "mcp", designMin: "L3", dual: false },
  { area: "AI apps", action: "Remove an AI app (new sign-ins only)", story: "S6.3", scope: "fleet", needs: "settings:write", product: "mcp", designMin: "L3", dual: false },

  // ---------------------------------------------------------------- Flags --
  { area: "Flags", action: "See configuration", story: "S6.0", scope: "fleet", needs: "settings:read", product: "mcp", designMin: "L1", dual: false },
  { area: "Flags", action: "Change a runtime setting", story: "S6.1", scope: "fleet", needs: "settings:write", product: "mcp", designMin: "L2", dual: false },
  { area: "Flags", action: "Apply settings — restarts the live service", story: "S6.1", scope: "fleet", needs: "service:restart", product: "mcp", designMin: "L3", dual: true },
  { area: "Flags", action: "Per-tenant flag override", story: "S6.2", scope: "tenant", needs: null, product: "mcp", designMin: "L2", dual: false, why: "MCP's settings are global — there is no per-tenant override route" },
  { area: "Flags", action: "Labeeb runtime flags", story: "S6.1", scope: "fleet", needs: null, product: "labeeb", designMin: "L3", dual: true, why: "no /labeeb route is mounted" },

  // -------------------------------------------------------------- Welcome --
  { area: "Welcome", action: "Edit draft greeting and chips", story: "S7.1–7.3", scope: "fleet-draft", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "no /labeeb route is mounted" },
  { area: "Welcome", action: "Publish to a test tenant", story: "S7.6", scope: "tenant", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "no /labeeb route is mounted" },
  { area: "Welcome", action: "Publish to the fleet", story: "S7.6", scope: "fleet", needs: null, product: "labeeb", designMin: "L3", dual: true, why: "no /labeeb route is mounted" },
  { area: "Welcome", action: "Revert to previous version", story: "S7.6", scope: "fleet", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "no /labeeb route is mounted" },

  // ----------------------------------------------------------------- HITL --
  { area: "HITL", action: "Per-tenant gating", story: "S8.1", scope: "tenant", needs: null, product: "labeeb", designMin: "L2", dual: false, why: "no /labeeb route is mounted" },
  { area: "HITL", action: "Remove a gate fleet-wide", story: "S8.2", scope: "fleet", needs: null, product: "labeeb", designMin: "L3", dual: true, why: "no /labeeb route is mounted" },

  // ------------------------------------------------------------ Governance --
  { area: "Governance", action: "Read the audit log", story: "S0.8", scope: "fleet", needs: "audit:read", product: "mcp", designMin: "L1", dual: false },
  { area: "Governance", action: "Grant or revoke a level", story: "S0.10", scope: "fleet", needs: null, product: "platform", designMin: "L4", dual: true, why: "group membership lives in Cognito and this gateway exposes no admin route for it" },
  { area: "Governance", action: "Break-glass elevation", story: "S0.9", scope: "any", needs: null, product: "platform", designMin: "L2", dual: false, why: "authorities come from the token's cognito:groups claim, fixed when it was minted — nothing this page does can change it" },
];

/**
 * The lowest level whose group holds this authority.
 *
 * Derived rather than declared, so it tracks GROUP_AUTHORITIES. Returns null
 * when nothing enforces the action here.
 */
export function enforcedMin(a: Action): Level | null {
  if (!a.needs) return null;
  let best: Level | null = null;
  for (const [group, auths] of Object.entries(GROUP_AUTHORITIES)) {
    if (!auths.includes(a.needs)) continue;
    const lvl = GROUP_LEVEL[group];
    if (!lvl) continue;
    if (best === null || LEVELS.indexOf(lvl) < LEVELS.indexOf(best)) best = lvl;
  }
  return best;
}

/**
 * Where the deployed pool and the design disagree about a rung, and which way.
 *
 *   "stricter"  the pool demands a HIGHER level than the design. An operator
 *               is refused a write the map promised them — annoying, visible,
 *               and safe.
 *
 *   "looser"    the pool grants the action at a LOWER level than the design
 *               says it should take. Nobody is refused, nothing looks wrong,
 *               and someone can do a thing the design reserved for a more
 *               senior person. This is the direction worth a warning, and it
 *               is the one nothing else in the console would ever surface.
 */
export type Divergence = "stricter" | "looser" | null;

export function divergence(a: Action): Divergence {
  const here = enforcedMin(a);
  if (here === null || a.designMin === "—") return null;
  const d = LEVELS.indexOf(here) - LEVELS.indexOf(a.designMin);
  if (d === 0) return null;
  return d > 0 ? "stricter" : "looser";
}

export function diverges(a: Action): boolean {
  return divergence(a) !== null;
}

/** Every area, in the order the actions declare them. */
export const AREAS: string[] = [...new Set(ACTIONS.map((a) => a.area))];

/** Authorities no action in this table names — a gap in one list or the other. */
export function unmappedAuthorities(): Authority[] {
  const used = new Set(ACTIONS.map((a) => a.needs).filter(Boolean));
  return AUTHORITIES.filter((x) => !used.has(x));
}
